import React, {useContext, useEffect, useState} from 'react';
import {Button, Input, Modal, Form, Select, Space, Tooltip, Typography, Popconfirm, Empty} from "antd";
import Tag from "../Tag/Tag";
import {connect, useDispatch} from 'react-redux';
import {SketchPicker} from "react-color";
import {setTags, addEntity, updateEntityLabels, removeEntity, changeConfig} from "../util/redux";
import {AuthContext} from "../Auth/AuthProvider";
import {getRandomColor} from "../util/shared_functions";
import {getEntities, saveConfig} from "../util/redux/asyncActions";
import {PlusOutlined, DeleteOutlined, QuestionCircleOutlined, SaveOutlined} from "@ant-design/icons";

const {Text} = Typography;

const select = state => {
    const tags = state.request.tags;
    const entities = state.request.entities;
    const config = state.request.config;
    const unsavedConfig = state.request.unsavedConfig;
    return {tags, entities, config, unsavedConfig};
};

function TagManager(props) {

    const {tags, entities, config, unsavedConfig} = props;
    const dispatch = useDispatch();
    const userManager = useContext(AuthContext);

    const updateConfig = (newEntities) => {
        try {
            const parsed = config ? JSON.parse(config) : {};
            parsed.entities = newEntities;
            dispatch(changeConfig(JSON.stringify(parsed, null, 2)));
        } catch (e) {
            // config not yet loaded or invalid — skip
        }
    };

    const [color, setColor] = useState(getRandomColor());
    const [isAddTagOpen, setIsAddTagOpen] = useState(false);
    const [tagName, setTagName] = useState("");
    const [entityLabels, setEntityLabels] = useState([]);

    useEffect(() => {
        dispatch(getEntities({userManager}));
    }, []);

    const handleColorChange = ({hex}) => {
        setColor(hex);
    };

    const handleAddTag = () => {
        setColor(getRandomColor());
        setTagName("");
        setEntityLabels([]);
        setIsAddTagOpen(true);
    };

    const handleOk = () => {
        if (tagName !== "") {
            const upperTag = tagName.toUpperCase();
            dispatch(setTags([...tags, {tag: upperTag, color}]));
            dispatch(addEntity({entityType: upperTag, labels: entityLabels}));
            const newEntities = {...entities, [upperTag]: {labels: entityLabels || [], color}};
            updateConfig(newEntities);
            setIsAddTagOpen(false);
            setEntityLabels([]);
        }
    };

    const handleCancel = () => {
        setIsAddTagOpen(false);
        setEntityLabels([]);
    };

    const handleRemoveTag = (index) => {
        const tag = tags[index];
        const newTags = tags.filter((_, i) => i !== index);
        dispatch(setTags(newTags));
        dispatch(removeEntity(tag.tag));
        const newEntities = {...entities};
        delete newEntities[tag.tag];
        updateConfig(newEntities);
    };

    const handleLabelsChange = (tagName, newLabels) => {
        dispatch(updateEntityLabels({entityType: tagName, labels: newLabels}));
        dispatch(addEntity({entityType: tagName, labels: newLabels}));
        const existingColor = tags.find(t => t.tag === tagName)?.color;
        const newEntities = {...entities, [tagName]: {labels: newLabels, ...(existingColor && {color: existingColor})}};
        updateConfig(newEntities);
    };

    const tableData = tags.map((tag, i) => ({
        key: i,
        index: i,
        tag: tag.tag,
        color: tag.color,
        labels: entities?.[tag.tag]?.labels || [],
    }));

    return (
        <div className="sc-page" style={{maxWidth: 900, paddingTop: 0}}>
            <div className="sc-panel">
                <div style={{display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8}}>
                    <div className="sc-sechead" style={{marginBottom: 0}}>
                        <h2>Entity tags</h2>
                        <p>what to look for · {tags.length}</p>
                    </div>
                    <Space>
                        <Button
                            icon={<SaveOutlined/>}
                            onClick={() => dispatch(saveConfig({userManager, config}))}
                            disabled={!unsavedConfig}
                            type={unsavedConfig ? "primary" : "default"}
                        >
                            Save
                        </Button>
                        <Button
                            icon={<PlusOutlined/>}
                            onClick={handleAddTag}
                        >
                            Add Tag
                        </Button>
                    </Space>
                </div>
                <Text type="secondary" style={{fontSize: 13, display: "block", marginBottom: 16}}>
                    Tags define the entity types used across the application.
                    In <strong>Prepare Data</strong>, use them to annotate training data.
                    In <strong>Configure</strong>, they map to detection rules.
                    Add <strong>labels</strong> to enable Dynamic Tagging — the system will detect entities matching those labels automatically.
                </Text>
                {tags.length === 0 ? (
                    <Empty
                        description="No tags defined yet. Add your first entity tag to get started."
                        style={{padding: "40px 0"}}
                    >
                        <Button type="primary" icon={<PlusOutlined/>} onClick={handleAddTag}>
                            Add Tag
                        </Button>
                    </Empty>
                ) : (
                    <div className="sc-rows">
                        <div className="sc-row head">
                            <span style={{minWidth: 190}}>Tag</span>
                            <span style={{display: "inline-flex", alignItems: "center", gap: 6}}>
                                Labels for Dynamic Tagging
                                <Tooltip title="Labels help Dynamic Tagging detect this entity type. Example: for PERSON add 'person', 'name', 'first_name'. Tags without labels are only used for manual tagging and Trained Tagging.">
                                    <QuestionCircleOutlined style={{color: "var(--sc-ink-3)"}}/>
                                </Tooltip>
                            </span>
                        </div>
                        {tableData.map((record) => (
                            <div className="sc-row" key={record.key}>
                                <span style={{display: "inline-flex", alignItems: "center", gap: 8, minWidth: 190, flex: "none"}}>
                                    <span style={{width: 10, height: 10, borderRadius: 3, background: record.color, flex: "none"}}/>
                                    <Tag
                                        tagName={record.tag}
                                        color={record.color}
                                        index={record.index}
                                        closable={false}
                                    />
                                </span>
                                <Select
                                    mode="tags"
                                    style={{flex: 1, minWidth: 200}}
                                    placeholder="Add labels for Dynamic Tagging..."
                                    value={record.labels}
                                    onChange={(newLabels) => handleLabelsChange(record.tag, newLabels)}
                                />
                                <Popconfirm
                                    title={`Remove "${record.tag}"?`}
                                    description="This will remove the tag and its labels."
                                    onConfirm={() => handleRemoveTag(record.index)}
                                >
                                    <Button type="text" danger icon={<DeleteOutlined/>} size="small"/>
                                </Popconfirm>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Modal
                title="Add a new Tag"
                open={isAddTagOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                okButtonProps={{disabled: !tagName}}
            >
                <Form layout="vertical" style={{marginTop: 16}}>
                    <Form.Item label="Name" required>
                        <Input
                            placeholder="e.g. PERSON, EMAIL, PHONE"
                            value={tagName}
                            onChange={(e) => setTagName(e.target.value)}
                        />
                    </Form.Item>

                    <Form.Item label="Color">
                        <SketchPicker
                            color={color}
                            onChangeComplete={handleColorChange}
                            onChange={handleColorChange}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <Space>
                                Labels
                                <Tooltip title="Optional. Labels for Dynamic Tagging to detect this entity type. Example: for PERSON add 'person', 'name', 'first_name'.">
                                    <QuestionCircleOutlined/>
                                </Tooltip>
                            </Space>
                        }
                    >
                        <Select
                            mode="tags"
                            placeholder="Type labels and press Enter..."
                            value={entityLabels}
                            onChange={setEntityLabels}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default connect(select)(TagManager);
